import PopupState, { bindHover, bindPopover } from "material-ui-popup-state";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import React, { FC, ReactNode, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import { ColorPrimary } from "@intelligems/ig-theme";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton } from "@mui/material";
import { PopoverOrigin } from "@mui/material/Popover/Popover";
import { ClipboardCopy } from "@src/utils/ClipboardCopy";

// https://github.com/jcoreio/material-ui-popup-state/issues/85
export function bindDelayedHover(popupState: any, delayMs = 200) {
  const { onTouchStart, onMouseOver, onMouseLeave, ...hoverAriaProps } =
    bindHover(popupState);

  const timeout = useRef<any | null>(null);

  const delayedMouseOver = useCallback(
    (e: React.MouseEvent) => {
      if (timeout.current) clearTimeout(timeout.current);

      // material-ui-popup-state's event handler uses currentTarget to set the anchorEl, but
      // currentTarget is only defined while the initial event is firing. Save the original
      // and set it again before calling the delayed event handler
      const { currentTarget } = e;
      timeout.current = setTimeout(() => {
        e.currentTarget = currentTarget;
        onMouseOver(e);
      }, delayMs);
    },
    [onMouseOver]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      if (timeout.current) clearTimeout(timeout.current);
      onMouseLeave(e);
    },
    [onMouseLeave]
  );

  return {
    onTouchStart,
    onMouseOver: delayedMouseOver,
    onMouseLeave: handleMouseLeave,
    ...hoverAriaProps,
  };
}

interface BasePopoverProps {
  id?: string;
  children: ReactNode;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  showIds?: boolean;
}

/**
 * @see Popover
 */
export const IdPopover: FC<BasePopoverProps> = ({
  id,
  transformOrigin,
  anchorOrigin,
  children,
  showIds,
}) => {
  // const showIds = useSelector(getShowIds);
  const timeout = useRef<any | null>(null);

  return (
    <PopupState variant="popover" popupId={`popover-id-${id}`}>
      {(popupState) => {
        const { onMouseOver, onMouseLeave, ...restBindHover } =
          bindDelayedHover(popupState, 500);

        return (
          <div>
            {React.cloneElement(children as any, {
              onMouseOver: (e) => {
                if (showIds) {
                  clearTimeout(timeout.current);
                  onMouseOver(e);
                }
              },
              onMouseLeave: (e) => {
                if (showIds) {
                  timeout.current = setTimeout(() => onMouseLeave(e), 1000);
                }
              },
              ...restBindHover,
            })}
            <HoverPopover
              {...bindPopover(popupState)}
              anchorOrigin={
                anchorOrigin || {
                  vertical: "bottom",
                  horizontal: "left",
                }
              }
              transformOrigin={
                transformOrigin || {
                  vertical: "top",
                  horizontal: "left",
                }
              }
              PaperProps={{
                sx: {
                  padding: "10px",
                  paddingLeft: "15px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 4px 0px rgba(140, 140, 140, 0.08)",
                  border: "1px solid rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <Box sx={{ marginTop: "5px" }}>
                <ClipboardCopy
                  copyText={id || ""}
                  copyTextSx={{ fontSize: "12px", color: ColorPrimary.main }}
                  copyButton={
                    <IconButton
                      size={"small"}
                      sx={{ marginLeft: "10px", color: ColorPrimary.main }}
                    >
                      <ContentCopyIcon sx={{ height: "16px", width: "16px" }} />
                    </IconButton>
                  }
                  codeFont={true}
                />
              </Box>
            </HoverPopover>
          </div>
        );
      }}
    </PopupState>
  );
};
